import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import DeletePopUp from "../DeletePopUp";
import { darkenColor } from "../../exports/functions";
import { Pencil } from "lucide-react";
import { IModules, IStatusManagementOption } from "../../exports/interfaces";
import { usePermission } from "../../hooks/usePermission";
import { useLoader } from "../../context/LoaderContext";
import axiosClient from "../../axioxClient.ts/axiosClient";
import { fetchStatusManagementOption } from "../../api/AdminApi";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";

export const getStatusClass = (status: string) => {
  switch (status) {
    case "Active":
      return "pill-success";
    case "Inactive":
      return "pill-danger";
    case "Deactivated":
      return "bg-secondary";
    default:
      return "bg-secondary";
  }
};

// const categories = [
//   "Cooperative Statuses",
//   "Complaint Statuses",
//   "Notice Statuses",
//   "User Statuses",
// ];

// const rows = [
//   {
//     id: 1,
//     label: "Agriculture",
//     value: "Agriculture Cooperative",
//     color: "#B5CAE5",
//     order: 1,
//     status: "Active",
//   },
//   {
//     id: 2,
//     label: "Multipurpose",
//     value: "Multipurpose Cooperative",
//     color: "#EFAE6D",
//     order: 2,
//     status: "Active",
//   },
//   {
//     id: 3,
//     label: "Saving and Credits",
//     value: "Saving and Credits Cooperative",
//     color: "#C2DD9F",
//     order: 3,
//     status: "Active",
//   },
//   {
//     id: 4,
//     label: "Other",
//     value: "Other Cooperative",
//     color: "#5C5C5C",
//     order: 4,
//     status: "Active",
//   },
// ];

const StatusManagement = ({
  statusOptions,
  setStatusOptions,
}: {
  statusOptions: IModules[];
  setStatusOptions: Dispatch<SetStateAction<IModules[]>>;
}) => {
  const { setLoading } = useLoader();
  const { canCreate, canEdit, canDelete } = usePermission();

  const [selectedModule, setSelectedModule] = useState<IModules | null>(null);
  const [tableRows, setTableRows] = useState<IStatusManagementOption[]>([]);
  const [showDelete, setShowDelete] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const [newLabel, setNewLabel] = useState("");
  const [newValue, setNewValue] = useState("");
  const [newColor, setNewColor] = useState("");
  const [newOrder, setNewOrder] = useState(0);
  const [newStatus, setNewStatus] = useState("Active");

  // Track if we've initialized selectedCategory
  const initialized = useRef(false);

  const handleCategorySelect = (module: IModules) => {
    setSelectedModule(module);
    setTableRows(module.statusOptions);
    const maxOrder =
      module.statusOptions && module.statusOptions.length
        ? Math.max(...module.statusOptions.map((d) => Number(d.order || 0)))
        : 0;
    setNewOrder(maxOrder + 1);
  };

  useEffect(() => {
    if (!initialized.current && statusOptions.length > 0) {
      initialized.current = true;
      const first = statusOptions[0];
      setSelectedModule(first);
      setTableRows(first.statusOptions);
      const maxOrder =
        first.statusOptions && first.statusOptions.length
          ? Math.max(...first.statusOptions.map((d) => Number(d.order || 0)))
          : 0;
      setNewOrder(maxOrder + 1);
    }
  }, [statusOptions]);

  const handleClose = () => {
    setShowDelete(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    setLoading(true);
    try {
      const res = await axiosClient.delete(
        `/options/status-option/${deleteId}`,
      );

      if (res.status === 200 || res.status === 201) {
        const modules = await fetchStatusManagementOption();
        setStatusOptions(modules);
        const updated =
          modules.find((c) => c.id === selectedModule?.id) || null;
        setSelectedModule(updated);
        setTableRows(updated?.statusOptions || []);
        dynamicSuccess("Status deleted successfully!");
      }
    } catch (err: any) {
      dynamicFailed(err.message);
    } finally {
      setTimeout(() => setLoading(false), 1000);
      setShowDelete(false);
      setDeleteId(null);
    }
  };

  const handleSaveEdit = async (id: number) => {
    const row = tableRows.find((r) => r.id === id);
    if (!row) return;

    try {
      setLoading(true);

      const payload = {
        label: row.label,
        value: row.value,
        color: row.color,
        order: row.order,
        status: row.status,
      };

      const res = await axiosClient.put(
        `/options/status-option/${id}`,
        payload,
      );

      if (res.status === 200 || res.status === 201) {
        const modules = await fetchStatusManagementOption();
        setStatusOptions(modules);
        const updated =
          modules.find((c) => c.id === selectedModule?.id) || null;
        setSelectedModule(updated);
        setTableRows(updated?.statusOptions || []);

        setEditingId(null);
        dynamicSuccess("Status updated successfully!");
      }
    } catch (err: any) {
      dynamicFailed(err.message);
    } finally {
      setTimeout(() => setLoading(false), 1000);
    }
  };

  const handleSubmit = async () => {
    if (!selectedModule) {
      dynamicFailed("Please select a module first!");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        label: newLabel,
        value: newValue,
        color: newColor,
        order: newOrder,
        status: newStatus,
        moduleId: selectedModule.id,
      };

      const res = await axiosClient.post("/options/status-option", payload);

      if (res.status === 200 || res.status === 201) {
        const modules = await fetchStatusManagementOption();
        setStatusOptions(modules);
        const updated =
          modules.find((c) => c.id === selectedModule?.id) || null;
        setSelectedModule(updated);
        setTableRows(updated?.statusOptions || []);

        setNewLabel("");
        setNewValue("");
        setNewColor("");
        setNewOrder(0);
        setNewStatus("Active");
        setShowForm(false);

        dynamicSuccess("Status added successfully!");
      }
    } catch (err: any) {
      dynamicFailed(err.message);
    } finally {
      setTimeout(() => setLoading(false), 1000);
    }
  };

  const handleRowChange = (
    id: number,
    field: "label" | "value" | "color",
    value: string,
  ) => {
    setTableRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    );
  };

  return (
    <>
      <div className="row g-3">
        {/* Left Sidebar */}
        <div className="col-lg-3 col-md-5">
          <div className="dashboard-card h-100 p-0 overflow-hidden">
            <h5 className="primary-color p-2 border-bottom ">Modules</h5>

            <div className="list-group list-group-flush custom-list-group">
              {statusOptions.map((option) => (
                <button
                  key={option.id}
                  className={`list-group-item list-group-item-action border-0 text-start ${
                    selectedModule?.id === option.id ? "active-category" : ""
                  }`}
                  onClick={() => handleCategorySelect(option)}
                >
                  {option.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right Content */}
        <div className="col-lg-9 col-md-7">
          <div className="dashboard-card">
            <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-2">
              <div>
                <h5 className="primary-color">{selectedModule?.name}</h5>
                <small>Manage available options for this dropdown field</small>
              </div>

              {canCreate("Admin Control") && (
                <button
                  className="btn btn-badge-primary px-3"
                  onClick={() => {
                    if (selectedModule) {
                      const maxOrder =
                        selectedModule.statusOptions &&
                        selectedModule.statusOptions.length
                          ? Math.max(
                              ...selectedModule.statusOptions.map((o) =>
                                Number(o.order || 0),
                              ),
                            )
                          : 0;
                      setNewOrder(maxOrder + 1);
                    }
                    setShowForm((prev) => !prev);
                  }}
                >
                  <i className="fa-solid fa-plus me-2"></i>
                  Add Option
                </button>
              )}
            </div>
            <div
              className={`option-form-wrapper dashboard-card ${showForm ? "open" : "p-0"}`}
            >
              <div className="option-form">
                <div className="row g-3">
                  <div className="col-md-6 col-lg-3">
                    <label className="form-label">Value (Slug)</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g Agriculture"
                      value={newValue}
                      onChange={(e) => setNewValue(e.target.value)}
                    />
                  </div>

                  <div className="col-md-6 col-lg-3">
                    <label className="form-label">Label (Display)</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g Agriculture"
                      value={newLabel}
                      onChange={(e) => setNewLabel(e.target.value)}
                    />
                  </div>

                  <div className="col-md-6 col-lg-3">
                    <label className="form-label">Color</label>
                    <div className="color-input-wrapper">
                      <input
                        type="color"
                        value={newColor}
                        onChange={(e) => setNewColor(e.target.value)}
                        className="color-circle"
                      />

                      <input
                        type="text"
                        value={newColor.toUpperCase()}
                        onChange={(e) => setNewColor(e.target.value)}
                        className="form-control color-text-input"
                        placeholder="e.g #B5CAE5"
                      />
                    </div>
                  </div>

                  <div className="col-md-6 col-lg-3">
                    <label className="form-label">Sort Order</label>
                    <input
                      type="number"
                      className="form-control"
                      value={newOrder}
                      onChange={(e) => setNewOrder(Number(e.target.value))}
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-2 mt-3">
                  <button
                    className="btn btn-badge-border px-3"
                    onClick={() => {
                      setShowForm(false);
                      setNewLabel("");
                      setNewValue("");
                      setNewColor("");
                      setNewOrder(0);
                      setNewStatus("Active");
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    className="btn btn-badge-primary px-3"
                    onClick={handleSubmit}
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            <div className="table-responsive admin-table table-checkbox">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Value</th>
                    <th>Color</th>
                    <th>Status</th>
                    <th>Order</th>
                    <th className="sticky-action">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {tableRows.map((row, index) => (
                    <tr key={index}>
                      <td>
                        {editingId === row.id ? (
                          <input
                            type="text"
                            className="form-control"
                            value={row.label}
                            onChange={(e) =>
                              handleRowChange(row.id, "label", e.target.value)
                            }
                          />
                        ) : (
                          row.label
                        )}
                      </td>
                      <td>
                        {editingId === row.id ? (
                          <input
                            type="text"
                            className="form-control"
                            value={row.value}
                            onChange={(e) =>
                              handleRowChange(row.id, "value", e.target.value)
                            }
                          />
                        ) : (
                          row.value
                        )}
                      </td>
                      <td>
                        {editingId === row.id ? (
                          <div className="color-input-wrapper">
                            <input
                              type="color"
                              value={row.color}
                              onChange={(e) =>
                                handleRowChange(row.id, "color", e.target.value)
                              }
                              className="color-circle"
                            />

                            <input
                              type="text"
                              value={row.color.toUpperCase()}
                              onChange={(e) =>
                                handleRowChange(row.id, "color", e.target.value)
                              }
                              className="form-control color-text-input"
                            />
                          </div>
                        ) : (
                          <div className="d-flex align-items-center gap-2">
                            <span
                              style={{
                                width: "20px",
                                height: "20px",
                                backgroundColor: row.color,
                                borderRadius: "50%",
                                display: "inline-block",
                                border: "1px solid #ddd",
                              }}
                            />
                            <small>{row.color}</small>
                          </div>
                        )}
                      </td>
                      <td>
                        <span
                          className={`badge rounded-pill px-3 py-2`}
                          style={{
                            backgroundColor: row.color,
                            color: darkenColor(row.color, 40),
                          }}
                        >
                          {row.label}
                        </span>
                      </td>
                      <td>{row.order}</td>

                      <td className="sticky-action">
                        <div className="d-flex gap-3 align-items-center">
                          {editingId === row.id ? (
                            <>
                              <i
                                className={`fa-solid fa-floppy-disk text-success ${
                                  canEdit("Admin Control") ? "cursor-pointer" : "opacity-50"
                                }`}
                                title={
                                  canEdit("Admin Control") ? "Save" : "No permission"
                                }
                                style={{
                                  pointerEvents: canEdit("Admin Control")
                                    ? "auto"
                                    : "none",
                                }}
                                onClick={() => {
                                  if (!canEdit("Admin Control")) return;
                                  handleSaveEdit(row.id);
                                }}
                              />

                              <i
                                className={`fa-solid fa-trash text-danger ${
                                  canDelete("Admin Control") ? "cursor-pointer" : "opacity-50"
                                }`}
                                title={
                                  canDelete("Admin Control") ? "Delete" : "No permission"
                                }
                                style={{
                                  pointerEvents: canDelete("Admin Control")
                                    ? "auto"
                                    : "none",
                                }}
                                onClick={() => {
                                  if (!canDelete("Admin Control")) return;
                                  setDeleteId(row?.id || 0);
                                  setShowDelete(true);
                                }}
                              />
                            </>
                          ) : (
                            <>
                              <i
                                className={`cursor-pointer ${
                                  canEdit("Admin Control") ? "" : "opacity-50"
                                }`}
                                title={
                                  canEdit("Admin Control") ? "Edit" : "No permission"
                                }
                                style={{
                                  pointerEvents: canEdit("Admin Control")
                                    ? "auto"
                                    : "none",
                                }}
                                onClick={() => {
                                  if (!canEdit("Admin Control")) return;
                                  setEditingId(row.id);
                                }}
                              >
                                <Pencil size={20} />
                              </i>
                              <i
                                className={`fa-solid fa-trash text-danger ${
                                  canDelete("Admin Control") ? "cursor-pointer" : "opacity-50"
                                }`}
                                title={
                                  canDelete("Admin Control") ? "Delete" : "No permission"
                                }
                                style={{
                                  pointerEvents: canDelete("Admin Control")
                                    ? "auto"
                                    : "none",
                                }}
                                onClick={() => {
                                  if (!canDelete("Admin Control")) return;
                                  setDeleteId(row?.id || 0);
                                  setShowDelete(true);
                                }}
                              />
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      {showDelete && (
        <DeletePopUp onClose={handleClose} onConfirm={handleDelete} />
      )}
    </>
  );
};

export default StatusManagement;
